"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NativeLink as Link } from "@/components/native-link";
import {
  Activity, CheckCircle2, ClipboardCheck, Clock3, ExternalLink,
  Filter, RefreshCw, Search, Stethoscope, XCircle,
} from "lucide-react";
import { Card, MetricCard, PageHeader, Pill } from "@/components/pulse-shell";
import {
  api,
  type CheckupProtocolMeta,
  type CheckupResult,
  type CheckupStats,
} from "@/lib/api";
import {
  CHECKUP_CATEGORY_LABEL,
  CHECKUP_PROTOCOLS,
  type CheckupCategory,
  type CheckupProtocol,
} from "@/lib/checkup-protocols";

type StatusTab = "all" | "failing" | "passing" | "never";
type FilterTab = StatusTab | CheckupCategory;

const STATUS_TABS = new Set<string>(["all", "failing", "passing", "never"]);
const CATEGORIES = new Set<string>(Object.keys(CHECKUP_CATEGORY_LABEL));

const emptyStats: CheckupStats = {
  totalResults: 0,
  passCount: 0,
  failCount: 0,
  passRate: null,
  failingProtocols: 0,
  passingProtocols: 0,
  neverRun: CHECKUP_PROTOCOLS.length,
  last14Days: [],
};

function statusOf(meta: CheckupProtocolMeta | undefined): "pass" | "fail" | "never" {
  if (!meta?.latestStatus) return "never";
  return meta.latestStatus;
}

function relativeTime(iso?: string | null): string {
  if (!iso) return "Never";
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return iso.slice(0, 16).replace("T", " ");
  const m = Math.floor(ms / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function parseTab(raw: string | null): FilterTab {
  if (!raw) return "all";
  if (STATUS_TABS.has(raw) || CATEGORIES.has(raw)) return raw as FilterTab;
  return "all";
}

export function CheckupsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [byProtocol, setByProtocol] = useState<Record<string, CheckupProtocolMeta>>({});
  const [results, setResults] = useState<CheckupResult[]>([]);
  const [stats, setStats] = useState<CheckupStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(() => searchParams.get("q") || "");

  const tab = parseTab(searchParams.get("status") || searchParams.get("category"));
  const priorityOnly = searchParams.get("priority") === "p0";

  function patchParams(patch: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === "" || v === "all") next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }

  function setTab(next: FilterTab) {
    if (STATUS_TABS.has(next)) {
      patchParams({ status: next === "all" ? null : next, category: null });
    } else {
      patchParams({ category: next, status: null });
    }
  }

  function setPriorityOnly(on: boolean) {
    patchParams({ priority: on ? "p0" : null });
  }

  async function load() {
    setLoading(true);
    try {
      const data = await api.listCheckups(CHECKUP_PROTOCOLS.length);
      setByProtocol(data.byProtocol || {});
      setResults(data.results || []);
      setStats({
        ...emptyStats,
        ...data.stats,
        neverRun: Math.max(
          0,
          CHECKUP_PROTOCOLS.length - Object.keys(data.byProtocol || {}).length,
        ),
      });
    } catch {
      setByProtocol({});
      setResults([]);
      setStats(emptyStats);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      patchParams({ q: query.trim() || null });
    }, 200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync q from local input
  }, [query]);

  const failingCount = useMemo(
    () => CHECKUP_PROTOCOLS.filter((p) => statusOf(byProtocol[p.id]) === "fail").length,
    [byProtocol],
  );
  const passingCount = useMemo(
    () => CHECKUP_PROTOCOLS.filter((p) => statusOf(byProtocol[p.id]) === "pass").length,
    [byProtocol],
  );
  const neverCount = CHECKUP_PROTOCOLS.length - failingCount - passingCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CHECKUP_PROTOCOLS.filter((p) => {
      if (priorityOnly && p.priority !== "p0") return false;
      const st = statusOf(byProtocol[p.id]);
      if (tab === "failing" && st !== "fail") return false;
      if (tab === "passing" && st !== "pass") return false;
      if (tab === "never" && st !== "never") return false;
      if (
        tab !== "all" && tab !== "failing" && tab !== "passing" && tab !== "never"
        && p.category !== tab
      ) return false;
      if (!q) return true;
      const hay = `${p.title} ${p.goal} ${p.category} ${CHECKUP_CATEGORY_LABEL[p.category]}`.toLowerCase();
      return hay.includes(q);
    });
  }, [byProtocol, query, tab, priorityOnly]);

  const chart = useMemo(() => buildPassFailChart(stats.last14Days || []), [stats.last14Days]);

  const categoryBreakdown = useMemo(() => {
    const cats: CheckupCategory[] = [
      "conversation", "sessions-list", "session-detail", "admin-ops", "regression",
    ];
    return cats.map((cat) => {
      const protocols = CHECKUP_PROTOCOLS.filter((p) => p.category === cat);
      const fail = protocols.filter((p) => statusOf(byProtocol[p.id]) === "fail").length;
      const pass = protocols.filter((p) => statusOf(byProtocol[p.id]) === "pass").length;
      const never = protocols.length - fail - pass;
      return { cat, label: CHECKUP_CATEGORY_LABEL[cat], total: protocols.length, fail, pass, never };
    });
  }, [byProtocol]);

  const failingProtocols = useMemo(
    () => CHECKUP_PROTOCOLS.filter((p) => statusOf(byProtocol[p.id]) === "fail"),
    [byProtocol],
  );

  return <>
    <PageHeader
      eyebrow="Quality assurance"
      title="Checkups"
      subtitle="Run QA protocols, record Pass/Fail with session evidence, and track fleet health over time."
      actions={<>
        <div className="search-wrap">
          <Search />
          <input
            className="field"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search protocols"
          />
        </div>
        <button
          type="button"
          className={`btn ${priorityOnly ? "primary" : ""}`}
          onClick={() => setPriorityOnly(!priorityOnly)}
        >
          <Filter />Critical only
        </button>
        <button type="button" className="btn" onClick={() => void load()}>
          <RefreshCw className={loading ? "spin" : ""} />Refresh
        </button>
      </>}
    />

    <div className="metrics checkup-metrics">
      <MetricCard
        label="Failing protocols"
        value={String(failingCount)}
        tone="red"
        icon={<XCircle />}
        foot={<><span className="delta">{failingCount ? "Needs attention" : "All clear"}</span><span>latest result</span></>}
      />
      <MetricCard
        label="Pass rate"
        value={stats.passRate != null ? `${stats.passRate}%` : "—"}
        tone="green"
        icon={<CheckCircle2 />}
        foot={<><span className="delta">{stats.passCount} pass</span><span>{stats.failCount} fail</span></>}
      />
      <MetricCard
        label="Total checkups"
        value={String(stats.totalResults)}
        tone="blue"
        icon={<ClipboardCheck />}
        foot={<><span className="delta">{passingCount} protocols OK</span><span>{neverCount} never run</span></>}
      />
      <MetricCard
        label="Coverage"
        value={`${Math.round(((passingCount + failingCount) / CHECKUP_PROTOCOLS.length) * 100)}%`}
        tone="purple"
        icon={<Stethoscope />}
        foot={<><span className="delta">{CHECKUP_PROTOCOLS.length} protocols</span><span>catalog</span></>}
      />
    </div>

    {failingProtocols.length > 0 && (
      <Card
        title="Currently failing"
        kicker={`${failingProtocols.length} protocol${failingProtocols.length === 1 ? "" : "s"}`}
        className="checkup-failing-banner"
        action={
          <button type="button" className="btn small" onClick={() => setTab("failing")}>
            View failing
          </button>
        }
      >
        <div className="checkup-failing-list">
          {failingProtocols.map((p) => {
            const meta = byProtocol[p.id];
            const latest = results.find((r) => r.protocolId === p.id);
            return (
              <Link key={p.id} className="checkup-failing-row" href={`/checkups/${p.id}`}>
                <XCircle size={16} color="#e14d5a" />
                <span>
                  <b>{p.title}</b>
                  <span className="cell-sub">
                    {latest?.notes
                      ? latest.notes.split("\n")[0].slice(0, 120)
                      : meta?.lastResultAt
                        ? `Last fail ${relativeTime(meta.lastResultAt)}`
                        : "Latest result is Fail"}
                  </span>
                </span>
                <Pill tone="red">Failing</Pill>
              </Link>
            );
          })}
        </div>
      </Card>
    )}

    <div className="checkup-top-grid">
      <Card
        title="Checkup activity · 14 days"
        kicker="Pass vs fail runs"
        className="checkup-chart-card"
        action={<Pill tone={failingCount ? "red" : "green"}>{failingCount ? `${failingCount} red` : "Healthy"}</Pill>}
      >
        <div className="checkup-chart">
          <svg viewBox="0 0 560 160" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="checkupPassFill" x1="0" y1="0" x2="0" y2="1">
                <stop stopColor="#0aa878" stopOpacity=".22" />
                <stop offset="1" stopColor="#0aa878" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="checkupFailFill" x1="0" y1="0" x2="0" y2="1">
                <stop stopColor="#e14d5a" stopOpacity=".18" />
                <stop offset="1" stopColor="#e14d5a" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={chart.passArea} fill="url(#checkupPassFill)" />
            <path d={chart.failArea} fill="url(#checkupFailFill)" />
            <path d={chart.passLine} fill="none" stroke="#0aa878" strokeWidth="2.2" />
            <path d={chart.failLine} fill="none" stroke="#e14d5a" strokeWidth="2.2" />
          </svg>
          <div className="checkup-chart-legend">
            <span><i style={{ background: "#0aa878" }} />Pass</span>
            <span><i style={{ background: "#e14d5a" }} />Fail</span>
            <span className="checkup-chart-max">peak {chart.max}/day</span>
          </div>
          <div className="checkup-chart-labels">
            {chart.labels.map((l) => <span key={l}>{l}</span>)}
          </div>
        </div>
      </Card>

      <Card title="By category" kicker="Latest status mix" className="checkup-cat-card">
        <div className="checkup-cat-list">
          {categoryBreakdown.map((c) => (
            <button
              key={c.cat}
              type="button"
              className={`checkup-cat-row ${tab === c.cat ? "active" : ""}`}
              onClick={() => setTab(c.cat)}
            >
              <span className="checkup-cat-name">{c.label}</span>
              <span className="checkup-cat-bars" aria-hidden="true">
                <span style={{ flex: Math.max(c.pass, 0.15), background: "#0aa878" }} />
                <span style={{ flex: Math.max(c.fail, 0.15), background: "#e14d5a" }} />
                <span style={{ flex: Math.max(c.never, 0.15), background: "#c5d0dd" }} />
              </span>
              <span className="checkup-cat-meta">
                {c.fail ? <b className="fail">{c.fail} fail</b> : <b className="ok">{c.pass} ok</b>}
                <span>/{c.total}</span>
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card title="Recent runs" kicker="Latest evidence" className="checkup-recent-card" action={
        <Link className="btn small" href="/checkups">All protocols</Link>
      }>
        <div className="checkup-recent-list">
          {results.slice(0, 8).map((r) => {
            const proto = CHECKUP_PROTOCOLS.find((p) => p.id === r.protocolId);
            return (
              <Link
                key={r.resultId}
                className="checkup-recent-row"
                href={`/checkups/${r.protocolId}?result=${encodeURIComponent(r.resultId)}`}
              >
                <span className={`checkup-result-dot ${r.status}`} />
                <span className="checkup-recent-body">
                  <b>{proto ? proto.title : r.protocolId}</b>
                  <span>{relativeTime(r.createdAt)}{r.sessionId ? ` · ${r.sessionId.slice(0, 8)}…` : ""}</span>
                </span>
                <Pill tone={r.status === "pass" ? "green" : "red"}>{r.status === "pass" ? "Pass" : "Fail"}</Pill>
              </Link>
            );
          })}
          {!results.length && (
            <div className="empty-state">No checkup results yet. Open a protocol and record your first Pass or Fail.</div>
          )}
        </div>
      </Card>
    </div>

    <Card
      title="Checkup protocols"
      kicker={`${filtered.length} of ${CHECKUP_PROTOCOLS.length} shown`}
      className="checkup-queue"
      action={
        <div className="segmented checkup-tabs">
          {([
            ["all", "All"],
            ["failing", `Failing ${failingCount || ""}`],
            ["passing", "Passing"],
            ["never", "Never run"],
          ] as const).map(([id, label]) => (
            <button key={id} type="button" className={tab === id ? "active" : ""} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
      }
    >
      <div className="checkup-grid">
        {filtered.map((p) => (
          <ProtocolCard key={p.id} protocol={p} meta={byProtocol[p.id]} />
        ))}
        {!filtered.length && (
          <div className="empty-state" style={{ gridColumn: "1 / -1" }}>
            No protocols match this filter.
          </div>
        )}
      </div>
    </Card>
  </>;
}

function ProtocolCard({ protocol, meta }: { protocol: CheckupProtocol; meta?: CheckupProtocolMeta }) {
  const st = statusOf(meta);
  return (
    <Link
      href={`/checkups/${protocol.id}`}
      className={`checkup-protocol-card status-${st}`}
    >
      <div className="checkup-protocol-top">
        <Pill tone="gray">{CHECKUP_CATEGORY_LABEL[protocol.category]}</Pill>
        <StatusPill status={st} />
      </div>
      <h3>{protocol.title}</h3>
      <p>{protocol.goal}</p>
      <div className="checkup-protocol-foot">
        <span><Clock3 size={12} />{protocol.estimatedMinutes}m</span>
        <span className={`priority-tag ${protocol.priority}`}>{protocol.priority === "p0" ? "Critical" : protocol.priority === "p1" ? "High" : "Normal"}</span>
      </div>
      <div className="checkup-protocol-meta">
        {meta?.totalCount
          ? <>
              <span>{meta.passCount} pass · {meta.failCount} fail</span>
              <span>{relativeTime(meta.lastResultAt)}</span>
            </>
          : <span>Not run yet</span>}
      </div>
      {protocol.links[0] && (
        <span className="checkup-protocol-link">
          <ExternalLink size={12} />{protocol.links[0].label}
        </span>
      )}
    </Link>
  );
}

function StatusPill({ status }: { status: "pass" | "fail" | "never" }) {
  if (status === "fail") return <Pill tone="red"><XCircle size={11} />Failing</Pill>;
  if (status === "pass") return <Pill tone="green"><CheckCircle2 size={11} />Passing</Pill>;
  return <Pill tone="gray"><Activity size={11} />Never run</Pill>;
}

function buildPassFailChart(days: Array<{ date: string; pass: number; fail: number; total: number }>) {
  const series = days.length
    ? days
    : Array.from({ length: 14 }, (_, i) => {
        const d = new Date(Date.now() - (13 - i) * 86400000);
        return { date: d.toISOString().slice(0, 10), pass: 0, fail: 0, total: 0 };
      });
  const max = Math.max(1, ...series.map((d) => Math.max(d.pass, d.fail, d.total)));
  const w = 560;
  const h = 140;
  const pad = 8;
  const n = Math.max(1, series.length - 1);

  const toPath = (key: "pass" | "fail") => {
    const pts = series.map((d, i) => {
      const x = (i / n) * w;
      const y = h - pad - ((d[key] / max) * (h - pad * 2));
      return [x, y] as const;
    });
    const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
    const area = `${line} L${w} ${h} L0 ${h} Z`;
    return { line, area };
  };

  const pass = toPath("pass");
  const fail = toPath("fail");
  const labels = [
    series[0]?.date.slice(5) || "",
    series[Math.floor(series.length / 2)]?.date.slice(5) || "",
    series[series.length - 1]?.date.slice(5) || "",
  ];
  return {
    passLine: pass.line,
    passArea: pass.area,
    failLine: fail.line,
    failArea: fail.area,
    max,
    labels,
  };
}
